import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { setupAxiosInterceptors } from "./utils/axiosConfig";
import SessionMonitor from "./components/SessionMonitor";
import DynamicCanonical from "./components/DynamicCanonical";
import MaintenancePage from "./pages/MaintenancePage";
import CookieConsent from "./components/CookieConsent";
import Home from "./pages/Home";
import MiamiComingSoon from "./pages/MiamiComingSoon";
import OnLocation from "./pages/OnLocation";
import AdminSetup from "./pages/AdminSetup";
import About from "./pages/About";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import LiveEventsLanding from "./pages/LiveEventsLanding";
import LiveEventDetail from "./pages/LiveEventDetail";
import LiveCustomerLogin from "./pages/LiveCustomerLogin";
import LiveMyTickets from "./pages/LiveMyTickets";
import LiveWatchStream from "./pages/LiveWatchStream";
import LivePaymentSuccess from "./pages/LivePaymentSuccess";
import LiveCustomerAccount from "./pages/LiveCustomerAccount";
import LivestreamAdminDashboard from "./pages/LivestreamAdminDashboard";
import LiveAuthCallback from "./pages/LiveAuthCallback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import DMCA from "./pages/DMCA";
import ContentPolicy from "./pages/ContentPolicy";
import CookiePolicy from "./pages/CookiePolicy";

// VOD Platform
import VODLanding from "./pages/VODLanding";
import VODBrowse from "./pages/VODBrowse";
import VODVideoDetail from "./pages/VODVideoDetail";
import VODWatch from "./pages/VODWatch";
import VODMembership from "./pages/VODMembership";
import VODMyLibrary from "./pages/VODMyLibrary";
import VODPaymentSuccess from "./pages/VODPaymentSuccess";
import VODAdminDashboard from "./pages/VODAdminDashboard";
import VODSeriesDetail from "./pages/VODSeriesDetail";
import VODCreatorPackage from "./pages/VODCreatorPackage";
import VODAccount from "./pages/VODAccount";
import VODReelView from "./pages/VODReelView";
import VODPrivacyPolicy from "./pages/VODPrivacyPolicy";
import VODTermsOfService from "./pages/VODTermsOfService";
import VODAbout from "./pages/VODAbout";
import VODContact from "./pages/VODContact";
import VODFAQ from "./pages/VODFAQ";
import VODBlog from "./pages/VODBlog";
import VODBlogPost from "./pages/VODBlogPost";

// LTV Studio Creator Page (separate branding)
import LTVStudioCreatorPage from "./pages/LTVStudioCreatorPage";

// Creator Channel
import CreatorChannelPage from "./pages/CreatorChannelPage";
import CreatorDashboard from "./pages/CreatorDashboard";
import CreatorPortalLogin from "./pages/CreatorPortalLogin";
import CreatorPortalDashboard from "./pages/CreatorPortalDashboard";

// Destination Choice
import ChooseDestination from "./pages/ChooseDestination";

// Health Check
import Health from "./pages/Health";

// Component that renders VODLanding on ltvvod.com, Home on other domains
// Check is done synchronously to prevent flash of wrong content
const HomeOrVOD = () => {
  // Synchronous check - runs immediately on first render
  const isLtvvod = typeof window !== 'undefined' && window.location.hostname.includes('ltvvod.com');
  
  return isLtvvod ? <VODLanding /> : <Home />;
};

// Redirect /vod/v/:slug to canonical /vod/video/:slug
const VideoSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/vod/video/${slug}`} replace />;
};

const API = process.env.REACT_APP_BACKEND_URL || '';

function App() {
  const [maintenance, setMaintenance] = useState(null);

  // Remove ltvvod-loading class on mount (for ALL routes)
  useEffect(() => {
    document.documentElement.classList.remove('ltvvod-loading');
  }, []);
  
  // Set up axios interceptors for session management
  useEffect(() => {
    setupAxiosInterceptors();
  }, []);

  // Check maintenance status from API
  useEffect(() => {
    fetch(`${API}/api/maintenance/status`)
      .then(r => r.json())
      .then(data => setMaintenance(data))
      .catch(() => setMaintenance({ enabled: false }));
  }, []);

  // Maintenance mode — block all access except /health and admin routes
  if (maintenance?.enabled) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/health" element={<Health />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/vod" element={<VODAdminDashboard />} />
            <Route path="/vod/admin" element={<VODAdminDashboard />} />
            <Route path="/admin/livestream" element={<LivestreamAdminDashboard />} />
            <Route path="*" element={
              <MaintenancePage 
                message={maintenance.message} 
                subMessage={maintenance.sub_message} 
              />
            } />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Global session monitor - shows popup when kicked out by another login */}
      <SessionMonitor />
      {/* GDPR Cookie Consent Banner */}
      <CookieConsent />
      
      <BrowserRouter>
        <DynamicCanonical />
        <Routes>
          {/* Health check route for deployment */}
          <Route path="/health" element={<Health />} />
          
          {/* Root path - show VODLanding on ltvvod.com, Home on other domains */}
          <Route path="/" element={<HomeOrVOD />} />
          <Route path="/miami-coming-soon" element={<MiamiComingSoon />} />
          <Route path="/on-location" element={<OnLocation />} />
          <Route path="/admin-setup" element={<AdminSetup />} />
          <Route path="/booking/oauth/callback" element={<AdminSetup />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/dmca" element={<DMCA />} />
          <Route path="/copyright" element={<DMCA />} />
          <Route path="/content-policy" element={<ContentPolicy />} />
          <Route path="/community-guidelines" element={<ContentPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          
          {/* Live Streaming Routes */}
          <Route path="/live" element={<LiveEventsLanding />} />
          <Route path="/live/event/:slug" element={<LiveEventDetail />} />
          <Route path="/event/:slug" element={<LiveEventDetail />} /> {/* Clean URL for sharing */}
          <Route path="/live/login" element={<LiveCustomerLogin />} />
          <Route path="/live/my-tickets" element={<LiveMyTickets />} />
          <Route path="/live/watch/:slug" element={<LiveWatchStream />} />
          <Route path="/live/payment-success" element={<LivePaymentSuccess />} />
          <Route path="/live/account" element={<LiveCustomerAccount />} />
          <Route path="/live/auth-callback" element={<LiveAuthCallback />} />
          <Route path="/admin/livestream" element={<LivestreamAdminDashboard />} />
          
          {/* Destination Choice - after login from homepage */}
          <Route path="/choose-destination" element={<ChooseDestination />} />
          
          {/* VOD Platform Routes */}
          <Route path="/vod" element={<VODLanding />} />
          <Route path="/vod/browse" element={<VODBrowse />} />
          <Route path="/vod/v/:slug" element={<VideoSlugRedirect />} />  {/* 301-style redirect to canonical URL */}
          <Route path="/vod/video/:videoId" element={<VODVideoDetail />} />
          <Route path="/vod/watch/:videoId" element={<VODWatch />} />
          <Route path="/vod/reel/:reelId" element={<VODReelView />} />  {/* Reel share URL */}
          <Route path="/vod/membership" element={<VODMembership />} />
          <Route path="/vod/my-library" element={<VODMyLibrary />} />
          <Route path="/vod/payment-success" element={<VODPaymentSuccess />} />
          <Route path="/vod/subscription-success" element={<VODPaymentSuccess />} />
          <Route path="/vod/series/:seriesId" element={<VODSeriesDetail />} />
          <Route path="/vod/creator-package" element={<VODCreatorPackage />} />
          <Route path="/vod/account" element={<VODAccount />} />
          {/* VOD Legal Pages */}
          <Route path="/vod/privacy-policy" element={<VODPrivacyPolicy />} />
          <Route path="/vod/privacy" element={<VODPrivacyPolicy />} />
          <Route path="/vod/terms-of-service" element={<VODTermsOfService />} />
          <Route path="/vod/terms" element={<VODTermsOfService />} />
          <Route path="/vod/about" element={<VODAbout />} />
          <Route path="/vod/contact" element={<VODContact />} />
          <Route path="/vod/faq" element={<VODFAQ />} />
          <Route path="/vod/help" element={<VODFAQ />} />
          <Route path="/vod/blog" element={<VODBlog />} />
          <Route path="/vod/blog/:slug" element={<VODBlogPost />} />
          <Route path="/vod/cookie-policy" element={<CookiePolicy />} />
          <Route path="/vod/dmca" element={<DMCA />} />
          <Route path="/vod/content-policy" element={<ContentPolicy />} />
          <Route path="/creator" element={<VODCreatorPackage />} /> {/* Short URL */}
          <Route path="/studio" element={<VODCreatorPackage />} /> {/* LTV Studio link - same design */}
          <Route path="/studio/creator" element={<VODCreatorPackage />} /> {/* Alternative URL */}
          <Route path="/admin/vod" element={<VODAdminDashboard />} />
          <Route path="/vod/admin" element={<VODAdminDashboard />} /> {/* Alternative URL */}
          
          {/* Creator Channel Routes */}
          <Route path="/c/:slug" element={<CreatorChannelPage />} />
          <Route path="/creator/dashboard" element={<CreatorDashboard />} />
          
          {/* Creator Portal (Read-Only Analytics) */}
          <Route path="/creator/login" element={<CreatorPortalLogin />} />
          <Route path="/creator/portal" element={<CreatorPortalDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
